import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import {
  NewsFeedItem,
  NewsFeedMode,
  NewsTagReference,
} from '../../app/services/api/news/newsConsumerTypes';
import { useNewsConsumerClient } from '../../hooks/api/useNewsConsumerClient';
import { NewsModal } from '../DashboardV2/components/NewsModal';
import { getDefaultNewsImage } from '../../assets';
import { useTranslation } from 'react-i18next';

const pageSize = 24;

export default function NewsPage() {
  const { t } = useTranslation(['drawer', 'notificationPreferences']);
  const client = useNewsConsumerClient();
  const [mode, setMode] = useState<NewsFeedMode>('Current');
  const [search, setSearch] = useState('');
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [tagId, setTagId] = useState<number | ''>('');
  const [publishedFrom, setPublishedFrom] = useState('');
  const [publishedTo, setPublishedTo] = useState('');
  const [page, setPage] = useState(1);
  const [items, setItems] = useState<NewsFeedItem[]>([]);
  const [availableTags, setAvailableTags] = useState<NewsTagReference[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [selected, setSelected] = useState<NewsFeedItem | null>(null);

  useEffect(() => {
    setPage(1);
  }, [mode, search, unreadOnly, tagId, publishedFrom, publishedTo]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(false);

    const timer = window.setTimeout(() => {
      client.getFeed({
        mode,
        search: search.trim() || undefined,
        tagId: tagId === '' ? undefined : tagId,
        publishedFromUtc: publishedFrom ? new Date(`${publishedFrom}T00:00:00Z`).toISOString() : undefined,
        publishedToUtc: publishedTo ? new Date(`${publishedTo}T23:59:59Z`).toISOString() : undefined,
        unreadOnly,
        page,
        pageSize,
      })
        .then(result => {
          if (cancelled) return;
          setItems(result.items);
          setTotalCount(result.totalCount);
          setAvailableTags(current => {
            const tags = new Map(current.map(tag => [tag.id, tag]));
            result.items.flatMap(item => item.tags).forEach(tag => tags.set(tag.id, tag));
            return Array.from(tags.values()).sort((a, b) => a.name.localeCompare(b.name));
          });
        })
        .catch(() => {
          if (!cancelled) {
            setLoadError(true);
            setItems([]);
            setTotalCount(0);
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [client, mode, search, unreadOnly, tagId, publishedFrom, publishedTo, page, refreshCounter]);

  const handleRead = useCallback((publicationId: number) => {
    setItems(current => current.map(item =>
      item.publicationId === publicationId
        ? { ...item, readState: { ...item.readState, isRead: true, isUpdated: false } }
        : item,
    ));
  }, []);

  const ordered = useMemo(
    () => [...items].sort((a, b) => {
      if (mode === 'Current' && a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
      return b.publishedAtUtc.localeCompare(a.publishedAtUtc);
    }),
    [items, mode],
  );

  return (
    <PageArea>
      <PageTitle subTitle={t('newsDescription')}>{t('newsTitle')}</PageTitle>

      <Stack spacing={2}
        sx={{ mb: 4 }}>
        <Stack direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          alignItems={{ md: 'center' }}>
          <Tabs value={mode}
            onChange={(_, value) => setMode(value)}>
            <Tab value="Current"
              label={t('newsCurrent')} />
            <Tab value="History"
              label={t('newsHistory')} />
          </Tabs>
          <TextField
            size="small"
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder={t('newsSearch')}
            sx={{ minWidth: { md: 280 } }}
          />
          <FormControl size="small"
            sx={{ minWidth: 180 }}>
            <InputLabel>{t('newsCategory')}</InputLabel>
            <Select
              label={t('newsCategory')}
              value={tagId}
              onChange={event => setTagId(event.target.value === '' ? '' : Number(event.target.value))}
            >
              <MenuItem value="">{t('newsAllCategories')}</MenuItem>
              {availableTags.map(tag => <MenuItem key={tag.id}
                value={tag.id}>{tag.name}</MenuItem>)}
            </Select>
          </FormControl>
          <Chip
            clickable
            color={unreadOnly ? 'primary' : 'default'}
            label={t('newsUnreadOnly')}
            onClick={() => setUnreadOnly(value => !value)}
          />
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }}
          spacing={2}>
          <TextField
            size="small"
            type="date"
            label={t('newsPublishedFrom')}
            InputLabelProps={{ shrink: true }}
            value={publishedFrom}
            onChange={event => setPublishedFrom(event.target.value)}
          />
          <TextField
            size="small"
            type="date"
            label={t('newsPublishedTo')}
            InputLabelProps={{ shrink: true }}
            value={publishedTo}
            onChange={event => setPublishedTo(event.target.value)}
          />
        </Stack>
      </Stack>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      ) : loadError ? (
        <Stack spacing={2}
          alignItems="flex-start">
          <Typography color="text.secondary">{t('newsLoadFailed')}</Typography>
          <Button
            variant="secondary"
            size="md"
            onClick={() => setRefreshCounter(value => value + 1)}
          >
            {t('notificationPreferences:retry')}
          </Button>
        </Stack>
      ) : ordered.length === 0 ? (
        <Typography color="text.secondary">
          {mode === 'Current' ? t('newsNoCurrent') : t('newsNoHistory')}
        </Typography>
      ) : (
        <>
          <Grid container
            spacing={3}>
            {ordered.map(item => (
              <Grid item
                xs={12}
                sm={6}
                lg={4}
                key={item.publicationId}>
                <Card sx={{ height: '100%' }}>
                  <CardActionArea sx={{ height: '100%' }}
                    onClick={() => setSelected(item)}>
                    <Box
                      component="img"
                      src={item.hero?.url || getDefaultNewsImage(Math.abs(item.newsItemId) % 4)}
                      alt={item.hero?.altText || item.title}
                      sx={{ width: '100%', height: 180, objectFit: 'cover' }}
                    />
                    <CardContent>
                      <Stack direction="row"
                        spacing={1}
                        sx={{ mb: 1, flexWrap: 'wrap' }}>
                        {!item.readState.isRead && (
                          <Chip
                            label={item.readState.isUpdated ? t('newsUpdated') : t('newsNew')}
                            size="small"
                            color="primary"
                          />
                        )}
                        {item.isPinned && mode === 'Current' && <Chip label={t('newsPinned')}
                          size="small" />}
                        {item.tags.slice(0, 2).map(tag => <Chip key={tag.id}
                          label={tag.name}
                          size="small" />)}
                      </Stack>
                      <Typography variant="textMD"
                        fontWeight={700}
                        sx={{ mb: 1 }}>{item.title}</Typography>
                      <Typography variant="textSM"
                        color="text.secondary">{item.summary}</Typography>
                      <Typography variant="textSM"
                        color="text.secondary"
                        sx={{ mt: 2 }}>
                        {new Date(item.publishedAtUtc).toLocaleDateString('en-GB')}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
          {totalCount > pageSize && (
            <Stack alignItems="center"
              sx={{ mt: 4 }}>
              <Pagination
                page={page}
                count={Math.ceil(totalCount / pageSize)}
                onChange={(_, nextPage) => setPage(nextPage)}
              />
            </Stack>
          )}
        </>
      )}

      <NewsModal
        open={selected !== null}
        item={selected}
        mode={mode}
        onRead={handleRead}
        onClose={() => setSelected(null)}
      />
    </PageArea>
  );
}
